import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'primereact/button';
import { Form, Field } from 'formik';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { renderAsyncSelector } from '../../../components/formik-components/FormAsyncSelector';
import { FormikCalendar, FormikCheckbox } from '../../../components/prime-formik';
import Debug from '../../../components/Debug';
import NewAssessementFormOptions from './NewAssessementFormOptions';
import { formikFormShape, intlShape } from '../../../components/prop-types';

const i18n = defineMessages({
  choose: {
    id: 'choose',
    defaultMessage: 'Choose',
  },
  notificationOnChange: {
    id: 'notification.asmt.changed',
    defaultMessage: 'Notify me by email when the status of assessment is changed',
  },
  sendIndividualReport: {
    id: 'superlink.send_individual_report_to_respondent',
    defaultMessage: 'Send Individual Report to respondent if distortion is acceptable',
  },
  reportWhenComplete: {
    id: 'assessment.assessmentCompleted',
    defaultMessage: 'Send automatic report(s) when assessment is completed',
  },
});

const NewAssessmentForm = ({
  isTempLoading,
  assessmentTemplates,
  searchTemp,
  handleSelectTemplate,
  formProps: { setFieldValue, isSubmitting },
  intl: { formatMessage },
  completedReportAction,
  activeLocales,
  jobSearching,
  jobs,
  searchJob,
  handleCancel,
  changeCompletedReportAction,
}) => {
  const notificationOnChangeText = formatMessage(i18n.notificationOnChange);
  const sendIndividualReportText = formatMessage(i18n.sendIndividualReport);
  const reportWhenCompleteText = formatMessage(i18n.reportWhenComplete);
  return (
    <Form>
      <div className="p-grid p-fluid">
        <div className="p-md-6">
          <div className="p-grid">
            <div className="p-col-12">
              <div className="form-group ">
                <label className="field-label" htmlFor="assessmentTemplate">
                  <FormattedMessage id="attrs.asmt_template" defaultMessage="Assessment Template" />
                </label>
                <Field
                  id="assessmentTemplate"
                  name="assessmentTemplate"
                  component={renderAsyncSelector}
                  processing={isTempLoading}
                  data={assessmentTemplates}
                  filter
                  valueField="id"
                  textField="title"
                  onSearch={searchTemp}
                  setFieldValue={(fieldName, selectedValue) => {
                    handleSelectTemplate(selectedValue);
                    setFieldValue(fieldName, selectedValue);
                    setFieldValue('targetJob', '');
                  }}
                  placeholder={`- ${formatMessage(i18n.choose)} -`}
                />
              </div>
            </div>
            <div className="p-col-12">
              <label className="field-label" htmlFor="expirationTime">
                <FormattedMessage id="assessment.timeLimit" defaultMessage="Time Limit" />{' '}
                <small>
                  <FormattedMessage id="assessment.duration" defaultMessage="(maximum 90 days)" />
                </small>
              </label>

              <Field
                name="expirationTime"
                id="expirationTime"
                component={FormikCalendar}
                dateFormat="yy-M-dd"
                setFieldValue={(fieldName, selectedValue) => {
                  setFieldValue(fieldName, selectedValue, false);
                }}
              />
            </div>
            <div className="p-col-12">
              <Field
                id="subscribed"
                name="subscribed"
                component={FormikCheckbox}
                labelText={notificationOnChangeText}
                setFieldValue={(fieldName, selectedValue) => {
                  setFieldValue(fieldName, selectedValue, false);
                }}
              />
            </div>
            <div className="p-col-12">
              <Field
                id="sendingIndividualReport"
                name="sendingIndividualReport"
                component={FormikCheckbox}
                labelText={sendIndividualReportText}
                setFieldValue={(fieldName, selectedValue) => {
                  setFieldValue(fieldName, selectedValue, false);
                }}
              />
            </div>
            <div className="p-col-12">
              <Field
                id="sendReportsWhenAssessmentIsComplete"
                name="sendReportsWhenAssessmentIsComplete"
                component={FormikCheckbox}
                labelText={reportWhenCompleteText}
                setFieldValue={(fieldName, selectedValue) => {
                  setFieldValue(fieldName, selectedValue, false);
                }}
                additionAction={changeCompletedReportAction}
              />
            </div>
          </div>
        </div>
      </div>
      {completedReportAction && (
        <NewAssessementFormOptions
          activeLocales={activeLocales}
          jobSearching={jobSearching}
          jobs={jobs}
          searchJob={searchJob}
        />
      )}
      <div className="p-grid">
        <div className="p-col">
          <div className="col-md-6" />
          <div className="col-md-6 pull-right text-right">
            <FormattedMessage id="actions.save" defaultMessage="Save">
              {(text) => (
                <Button icon={isSubmitting ? 'pi pi-check' : ''} type="submit" label={text} disabled={isSubmitting} />
              )}
            </FormattedMessage>
            &nbsp;
            <FormattedMessage id="actions.cancel" defaultMessage="Cancel">
              {(text) => (
                <Button
                  onClick={() => handleCancel()}
                  className="p-button-secondary"
                  type="button"
                  label={text}
                  disabled={isSubmitting}
                />
              )}
            </FormattedMessage>
          </div>
        </div>
      </div>
      <Debug />
    </Form>
  );
};

NewAssessmentForm.propTypes = {
  isTempLoading: PropTypes.bool.isRequired,
  assessmentTemplates: PropTypes.arrayOf(PropTypes.any).isRequired,
  searchTemp: PropTypes.func.isRequired,
  handleSelectTemplate: PropTypes.func.isRequired,
  formProps: formikFormShape.isRequired,
  intl: intlShape.isRequired,
  completedReportAction: PropTypes.bool.isRequired,
  activeLocales: PropTypes.arrayOf(PropTypes.any).isRequired,
  jobSearching: PropTypes.bool.isRequired,
  jobs: PropTypes.arrayOf(PropTypes.any).isRequired,
  searchJob: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  changeCompletedReportAction: PropTypes.func.isRequired,
};

export default injectIntl(NewAssessmentForm);
